import { useCallback } from 'react';
import { getCurrentLocale } from '@/shared/i18n/config';

export interface TimeSlot {
  start: Date;
  end: Date;
  duration: number; // in minutes
}

export interface DateRange {
  start: Date;
  end: Date;
}

export const useDateCalculations = () => {
  // Round time to nearest interval (default 15 minutes)
  const roundToNearest = useCallback((date: Date, intervalMinutes: number = 15): Date => {
    const minutes = date.getMinutes();
    const roundedMinutes = Math.round(minutes / intervalMinutes) * intervalMinutes;
    const newDate = new Date(date);
    newDate.setMinutes(roundedMinutes);
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);
    return newDate;
  }, []);

  // Calculate duration between two dates in minutes
  const getDurationMinutes = useCallback((start: Date, end: Date): number => {
    return Math.abs(end.getTime() - start.getTime()) / (1000 * 60);
  }, []);

  // Convert pixels to time based on hour height
  const pixelsToMinutes = useCallback((pixels: number, hourHeight: number = 64): number => {
    return (pixels / hourHeight) * 60;
  }, []);

  // Convert time to pixels based on hour height
  const minutesToPixels = useCallback((minutes: number, hourHeight: number = 64): number => {
    return (minutes / 60) * hourHeight;
  }, []);

  // Get time from pixel position (for calendar grid positioning)
  const getTimeFromPosition = useCallback((
    pixels: number, 
    baseDate: Date, 
    hourHeight: number = 64
  ): Date => {
    const totalMinutes = pixelsToMinutes(pixels, hourHeight);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    const result = new Date(baseDate);
    result.setHours(Math.max(0, Math.min(23, hours)));
    result.setMinutes(Math.max(0, Math.min(59, minutes)));
    result.setSeconds(0);
    result.setMilliseconds(0);
    
    return result;
  }, [pixelsToMinutes]);

  // Get position from time (for calendar grid positioning)
  const getPositionFromTime = useCallback((
    time: Date, 
    baseDate: Date, 
    hourHeight: number = 64
  ): number => {
    const timeMs = time.getTime();
    const baseDateStart = new Date(baseDate);
    baseDateStart.setHours(0, 0, 0, 0);
    
    const diffMs = timeMs - baseDateStart.getTime();
    const diffMinutes = diffMs / (1000 * 60);
    
    return minutesToPixels(diffMinutes, hourHeight);
  }, [minutesToPixels]);

  // Check if two date ranges overlap
  const isOverlapping = useCallback((range1: DateRange, range2: DateRange): boolean => {
    return range1.start < range2.end && range2.start < range1.end;
  }, []);

  // Get overlap duration between two date ranges
  const getOverlapMinutes = useCallback((range1: DateRange, range2: DateRange): number => {
    if (!isOverlapping(range1, range2)) return 0;
    
    const overlapStart = new Date(Math.max(range1.start.getTime(), range2.start.getTime()));
    const overlapEnd = new Date(Math.min(range1.end.getTime(), range2.end.getTime()));
    
    return getDurationMinutes(overlapStart, overlapEnd);
  }, [isOverlapping, getDurationMinutes]);

  // Check if a time is within business hours
  const isBusinessHours = useCallback((
    time: Date, 
    startHour: number = 9, 
    endHour: number = 17
  ): boolean => {
    const hour = time.getHours();
    const minute = time.getMinutes();
    const timeInMinutes = hour * 60 + minute;
    const startMinutes = startHour * 60;
    const endMinutes = endHour * 60;
    
    return timeInMinutes >= startMinutes && timeInMinutes < endMinutes;
  }, []);

  // Get business hours for a given date
  const getBusinessHours = useCallback((
    date: Date, 
    startHour: number = 9, 
    endHour: number = 17
  ): DateRange => {
    const start = new Date(date);
    start.setHours(startHour, 0, 0, 0);
    
    const end = new Date(date);
    end.setHours(endHour, 0, 0, 0);
    
    return { start, end };
  }, []);

  // Check if date is weekend
  const isWeekend = useCallback((date: Date): boolean => {
    const day = date.getDay();
    return day === 0 || day === 6; // Sunday or Saturday
  }, []);

  // Get start and end of week for a given date
  const getWeekRange = useCallback((date: Date, startOnMonday: boolean = true): DateRange => {
    const start = new Date(date);
    const day = start.getDay();
    const diff = startOnMonday 
      ? (day === 0 ? -6 : 1 - day) // Monday start
      : -day; // Sunday start
    
    start.setDate(start.getDate() + diff);
    start.setHours(0, 0, 0, 0);
    
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    
    return { start, end };
  }, []);

  // Get start and end of month for a given date
  const getMonthRange = useCallback((date: Date): DateRange => {
    const start = new Date(date.getFullYear(), date.getMonth(), 1);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
    
    return { start, end };
  }, []);

  // Format time duration in human readable format
  const formatDuration = useCallback((minutes: number): string => {
    if (minutes < 60) {
      return `${minutes}m`;
    } else if (minutes < 1440) { // Less than a day
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    } else {
      const days = Math.floor(minutes / 1440);
      const remainingHours = Math.floor((minutes % 1440) / 60);
      return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
    }
  }, []);

  // Add working days to a date (excluding weekends)
  const addWorkingDays = useCallback((date: Date, days: number): Date => {
    const result = new Date(date);
    let daysToAdd = days;
    
    while (daysToAdd > 0) {
      result.setDate(result.getDate() + 1);
      if (!isWeekend(result)) {
        daysToAdd--;
      }
    }
    
    return result;
  }, [isWeekend]);

  // Calculate working days between two dates
  const getWorkingDaysBetween = useCallback((start: Date, end: Date): number => {
    const current = new Date(start);
    let workingDays = 0;
    
    while (current <= end) {
      if (!isWeekend(current)) {
        workingDays++;
      }
      current.setDate(current.getDate() + 1);
    }
    
    return workingDays;
  }, [isWeekend]);

  // Format date as YYYY-MM-DD without timezone conversion
  const formatDateString = useCallback((date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // Format month and year for display
  const formatMonthYear = useCallback((date: Date, format: 'month' | 'day' = 'month'): string => {
    const locale = getCurrentLocale();
    if (format === 'day') {
      return new Intl.DateTimeFormat(locale, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }).format(date);
    }
    return new Intl.DateTimeFormat(locale, {
      month: 'long',
      year: 'numeric',
    }).format(date);
  }, []);

  // Format date range for display
  const formatDateRange = useCallback((start: Date, end: Date): string => {
    const locale = getCurrentLocale();
    const formatter = new Intl.DateTimeFormat(locale, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    return `${formatter.format(start)} - ${formatter.format(end)}`;
  }, []);

  // Get days in month with empty cells for calendar grid
  const getDaysInMonth = useCallback((date: Date): (Date | null)[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days: (Date | null)[] = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  }, []);

  return {
    roundToNearest,
    getDurationMinutes,
    pixelsToMinutes,
    minutesToPixels,
    getTimeFromPosition,
    getPositionFromTime,
    isOverlapping,
    getOverlapMinutes,
    isBusinessHours,
    getBusinessHours,
    isWeekend,
    getWeekRange,
    getMonthRange,
    formatDuration,
    addWorkingDays,
    getWorkingDaysBetween,
    formatDateString,
    formatMonthYear,
    formatDateRange,
    getDaysInMonth
  };
};
